import { addonsApi, useAddons, type AddonMeta } from '../addons';
import { useEffect, useMemo, useState } from 'react';
import { PageHub } from '../components/PageHub';
import { iptv, type EpgProgramme, type IptvAccount, type IptvChannel, type SportEvent } from '../api';
import { LeagueTile } from '../components/Widgets';
import { loadLeagues, loadWeek, summarize, type LeagueSummary } from '../sport';
import { BellIcon, ChannelLogo, ChannelName, channelDisplay, clock, dayLabel, LiveBadge, playLink, SportRow } from '../components/LiveBits';
import { hasReminder, toggleReminder, useReminders } from '../reminders';
import { linkProps, navigate } from '../router';

/** Live TV: sport (NL bovenaan) en de programmagids. */
export function Live({ tab }: { tab: 'sport' | 'guide' }) {
  const [accounts, setAccounts] = useState<IptvAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    iptv.accounts().then((r) => setAccounts(r.accounts)).catch(() => setError('IPTV-accounts laden mislukt.'));
  }, []);

  const usable = accounts?.filter((a) => a.enabledForProfile) ?? [];

  return (
    <main className="page">
      <PageHub page="live" />
      <header className="page-head">
        <h1>Live TV</h1>
        <a className="nav-item" {...linkProps('/settings/iptv')} style={{ marginLeft: 'auto' }}>IPTV-instellingen</a>
      </header>

      {error && <p className="placeholder-row error" role="alert">{error}</p>}

      {accounts && usable.length === 0 && (
        <div className="empty-card">
          <h2>{accounts.length === 0 ? 'Nog geen IPTV ingesteld' : 'IPTV staat uit voor dit profiel'}</h2>
          <p className="muted lead">
            {accounts.length === 0
              ? 'Vul de gegevens van je Xtream-provider in. Ze worden versleuteld op je eigen server bewaard.'
              : 'Zet IPTV voor dit profiel aan bij de IPTV-instellingen.'}
          </p>
          <a className="btn btn-primary" data-autofocus {...linkProps('/settings/iptv')}>Naar IPTV-instellingen</a>
        </div>
      )}

      {usable.length > 0 && (
        <>
          <div className="filterbar live-seg" role="tablist" aria-label="Weergave" data-focus-group>
            <a role="tab" className="chip" aria-selected={tab === 'sport'} aria-pressed={tab === 'sport'} {...linkProps('/live')}>Sport</a>
            <a role="tab" className="chip" aria-selected={tab === 'guide'} aria-pressed={tab === 'guide'} {...linkProps('/live/guide')}>Gids</a>
            <a className="chip" {...linkProps('/live/multi')}>Multiview</a>
          </div>
          {tab === 'sport' ? <SportTab /> : <GuideTab />}
        </>
      )}
      <div className="footer-space" />
    </main>
  );
}

// ---------- Sport ----------

function SportTab() {
  const [events, setEvents] = useState<SportEvent[] | null>(null);
  const [others, setOthers] = useState<Awaited<ReturnType<typeof iptv.sport>>['others']>([]);
  const [clubs, setClubs] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [leagues, setLeagues] = useState<LeagueSummary[]>([]);
  const addonState = useAddons();
  const [addonLive, setAddonLive] = useState<AddonLiveItem[]>([]);
  useEffect(() => {
    if (!addonState?.allowed || !addonState.addons.some((a) => a.kind === 'video' && a.streams)) return;
    addonsApi.live().then((r) => setAddonLive(r.items)).catch(() => setAddonLive([]));
  }, [addonState]);
  useEffect(() => {
    Promise.all([loadLeagues(), loadWeek()])
      .then(([ls, evs]) => setLeagues(summarize(ls, evs).filter((x) => x.week > 0)))
      .catch(() => undefined);
  }, []);

  const load = () =>
    iptv.sport(36)
      .then((r) => {
        setEvents(r.events);
        setOthers(r.others ?? []);
        setClubs(r.favoriteClubs.join(', '));
        setErrors(r.errors.map((e) => e.message));
      })
      .catch(() => setErrors(['Sportgids laden mislukt.']));

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const saveClubs = async () => {
    setSaving(true);
    await iptv.setFavoriteClubs(clubs.split(',')).catch(() => undefined);
    setSaving(false);
    void load();
  };

  const favorites = events?.filter((e) => e.favorite) ?? [];
  const live = events?.filter((e) => e.live && !e.favorite) ?? [];
  const upcoming = events?.filter((e) => !e.live && !e.favorite) ?? [];
  const byDay = useMemo(() => {
    const m = new Map<string, SportEvent[]>();
    for (const e of upcoming) {
      const k = dayLabel(e.start);
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return [...m.entries()];
  }, [upcoming]);

  return (
    <>
      {errors.map((e) => <p key={e} className="placeholder-row error" role="alert">{e}</p>)}
      {events === null && <p className="placeholder-row">Sportgids laden… De eerste keer kan dit even duren: de gids van je provider wordt opgehaald.</p>}
      {leagues.length > 0 && (
        <section className="row" aria-label="Competities">
          <h2>Competities</h2>
          <div className="row-track league-track" data-focus-group>
            {leagues.map((s) => <LeagueTile key={s.league.id} s={s} />)}
          </div>
        </section>
      )}
      {events?.length === 0 && <p className="placeholder-row">Geen sport gevonden in de gids van de komende 36 uur.</p>}

      {favorites.length > 0 && (
        <section className="sport-section" aria-label="Jouw clubs">
          <h2>Jouw clubs</h2>
          {favorites.map((e) => <SportRowPlus key={e.id} e={e} addon={addonLive} />)}
        </section>
      )}
      {live.length > 0 && (
        <section className="sport-section" aria-label="Nu live">
          <h2>Nu live</h2>
          {live.map((e) => <SportRowPlus key={e.id} e={e} addon={addonLive} />)}
        </section>
      )}
      {byDay.map(([day, list]) => (
        <section key={day} className="sport-section" aria-label={day}>
          <h2>{day}</h2>
          {list.map((e) => <SportRowPlus key={e.id} e={e} addon={addonLive} />)}
        </section>
      ))}

      {addonLive.length > 0 && (
        <section className="sport-section" aria-label="Uit je addons">
          <h2>Uit je addons</h2>
          <div className="ad-live-grid" data-focus-group>
            {addonLive.slice(0, 60).map((m) => (
              <a key={`${m.addonId}:${m.id}`} className="ad-live-item" {...linkProps(`/addon/${m.addonId}/${encodeURIComponent(m.type)}/${encodeURIComponent(m.id)}`)}>
                <span className="ad-live-art" style={m.poster ? { backgroundImage: `url("${m.poster}")` } : undefined} />
                <span className="other-body">
                  <strong className="other-title">{m.name}</strong>
                  {m.description && <span className="muted other-sub clamp-2">{m.description}</span>}
                  <span className="subtle">{[m.releaseInfo, m.addonName].filter(Boolean).join(' · ')}</span>
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="sport-section" aria-label="Nu op je sportzenders">
          <h2>Nu op je sportzenders</h2>
          <p className="subtle hint">Uitzendingen die niet als voetbal of F1 herkend zijn.</p>
          <div className="others-grid" data-focus-group>
            {others.map((o) => (
              <a key={`${o.channel.accountId}:${o.channel.streamId}`} className="other-item" {...linkProps(playLink(o.channel.accountId, o.channel.streamId))}>
                <ChannelLogo src={o.channel.logo} name={o.channel.name} size={32} />
                <span className="other-body">
                  <ChannelName name={o.channel.name} className="subtle" />
                  <strong className="other-title">{o.title}</strong>
                  {o.subTitle && <span className="muted other-sub">{o.subTitle}</span>}
                  <span className="subtle">{clock(o.start)}–{clock(o.stop)}</span>
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

      <form
        className="clubs-form"
        onSubmit={(e) => {
          e.preventDefault();
          void saveClubs();
        }}
      >
        <label htmlFor="clubs">Favoriete clubs (komen bovenaan, gescheiden door komma's)</label>
        <div style={{ display: 'flex', gap: 'calc(var(--u) * 1)', flexWrap: 'wrap' }}>
          <input id="clubs" className="text-input" value={clubs} onChange={(e) => setClubs(e.target.value)} placeholder="bijv. PSV, Ajax, Max Verstappen" />
          <button className="btn" type="submit" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</button>
        </div>
      </form>
    </>
  );
}

type AddonLiveItem = AddonMeta & { addonId: string; addonName: string };

const norm = (x: string) => x.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\b(fc|afc|sc|cf|ac|bv|the)\b/g, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

/** Wedstrijd uit de gids, met daaronder dezelfde wedstrijd uit je addons (op teamnamen). */
function SportRowPlus({ e, addon }: { e: SportEvent; addon: AddonLiveItem[] }) {
  const names = [e.match?.home?.name, e.match?.away?.name].filter((x): x is string => !!x).map(norm).filter((x) => x.length > 2);
  const hits = names.length === 2 ? addon.filter((m) => {
    const n = norm(m.name);
    return names.every((t) => n.includes(t) || n.includes(t.split(' ')[0]));
  }).slice(0, 4) : [];
  return (
    <>
      <SportRow e={e} />
      {hits.length > 0 && (
        <div className="ad-sport-chips" data-focus-group>
          <span className="subtle">Ook in je addons:</span>
          {hits.map((m) => <a key={`${m.addonId}:${m.id}`} className="chip" {...linkProps(`/addon/${m.addonId}/${encodeURIComponent(m.type)}/${encodeURIComponent(m.id)}`)}>{m.addonName}</a>)}
        </div>
      )}
    </>
  );
}

// ---------- Gids ----------

const PAGE = 40;
const WINDOW_MS = 4 * 3600_000;
const STEP_MS = 3 * 3600_000;

type Sel = { key: string; start: number } | null;

const StarIcon = ({ on, size = 18 }: { on: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={on ? 0 : 2}>
    <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
  </svg>
);

function GuideTab() {
  const [channels, setChannels] = useState<IptvChannel[] | null>(null);
  const [categories, setCategories] = useState<{ id: string; accountId: string; name: string }[]>([]);
  const [filter, setFilter] = useState<string>('fav'); // 'fav' | 'recent' | 'all' | categorie-id
  const [recent, setRecent] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [epg, setEpg] = useState<Record<string, EpgProgramme[]>>({});
  const [sel, setSel] = useState<Sel>(null);
  // Hoeveel stappen van 3 uur vooruit/terug (0 = nu)
  const [shift, setShift] = useState(0);
  useReminders();
  const [errors, setErrors] = useState<string[]>([]);
  const [now, setNow] = useState(Date.now());
  // Zendernummers: positie in de volledige lijst (in te toetsen in de live-player)
  const numbers = useMemo(() => new Map((channels ?? []).map((c, i) => [`${c.accountId}:${c.streamId}`, i + 1])), [channels]);

  useEffect(() => {
    iptv.channels()
      .then((r) => {
        setChannels(r.channels);
        setCategories(r.categories);
        setErrors(r.errors.map((e) => e.message));
        setRecent(r.recent ?? []);
        if (!r.channels.some((c) => c.favorite)) setFilter((r.recent ?? []).length ? 'recent' : 'all');
      })
      .catch(() => setErrors(['Kanalen laden mislukt.']));
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const from = Math.floor(now / 1800_000) * 1800_000 - 1800_000 + shift * STEP_MS; // halfuur terug, op het halfuur
  const to = from + WINDOW_MS;

  const keyOf = (c: IptvChannel) => `${c.accountId}:${c.streamId}`;

  const visible = useMemo(() => {
    if (!channels) return [];
    const q = query.trim().toLowerCase();
    let list = channels;
    if (filter === 'fav') list = list.filter((c) => c.favorite);
    else if (filter === 'recent') {
      const pos = new Map(recent.map((k, i) => [k, i]));
      list = list.filter((c) => pos.has(keyOf(c))).sort((a, b) => pos.get(keyOf(a))! - pos.get(keyOf(b))!);
    } else if (filter !== 'all') list = list.filter((c) => `${c.accountId}:${c.categoryId}` === filter);
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q) || c.categoryName.toLowerCase().includes(q));
    return list;
  }, [channels, filter, query, recent]);

  const shown = visible.slice(0, limit);

  // Gids ophalen voor de kanalen die in beeld staan, per account
  const shownKey = shown.map(keyOf).join(',');
  useEffect(() => {
    if (!shown.length) return;
    const byAccount = new Map<string, number[]>();
    for (const c of shown) byAccount.set(c.accountId, [...(byAccount.get(c.accountId) ?? []), c.streamId]);
    let cancelled = false;
    Promise.all(
      [...byAccount.entries()].map(([acc, ids]) =>
        iptv.epg(acc, ids, from, to).then((r) => Object.entries(r.programmes).map(([sid, list]) => [`${acc}:${sid}`, list] as const)),
      ),
    )
      .then((parts) => {
        if (!cancelled) setEpg((prev) => ({ ...prev, ...Object.fromEntries(parts.flat()) }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey, from]);

  const toggleFav = (c: IptvChannel) => {
    setChannels((list) => list?.map((x) => (x === c ? { ...x, favorite: !x.favorite } : x)) ?? null);
    void iptv.setFavorite(c.accountId, c.streamId, !c.favorite);
  };

  const ticks = Array.from({ length: WINDOW_MS / 1800_000 }, (_, i) => from + i * 1800_000);
  const pct = (t: number) => `${(Math.max(0, Math.min(1, (t - from) / WINDOW_MS)) * 100).toFixed(3)}%`;
  const nowInView = now >= from && now < to;

  // Mappen met het aantal kanalen erin
  const usedCats = useMemo(() => {
    const count = new Map<string, number>();
    for (const c of channels ?? []) count.set(`${c.accountId}:${c.categoryId}`, (count.get(`${c.accountId}:${c.categoryId}`) ?? 0) + 1);
    return categories.filter((c) => count.has(c.id)).map((c) => ({ ...c, n: count.get(c.id)! }));
  }, [categories, channels]);
  const favCount = channels?.filter((c) => c.favorite).length ?? 0;
  const recentCount = channels ? recent.filter((k) => channels.some((c) => keyOf(c) === k)).length : 0;

  // Uitgelicht: het gekozen programma, anders wat er nu op de eerste zender is
  const featured = useMemo(() => {
    const pick = (c: IptvChannel, p: EpgProgramme | null) => ({ c, p });
    if (sel) {
      const c = shown.find((x) => keyOf(x) === sel.key);
      if (c) return pick(c, (epg[sel.key] ?? []).find((p) => p.start === sel.start) ?? null);
    }
    const c = shown[0];
    if (!c) return null;
    return pick(c, (epg[keyOf(c)] ?? []).find((p) => p.start <= now && p.stop > now) ?? null);
  }, [sel, shown, epg, now]);

  const choose = (c: IptvChannel, p: EpgProgramme) => {
    const k = keyOf(c);
    // Tweede klik op hetzelfde programma = afspelen (of herinnering bij later)
    if (sel?.key === k && sel.start === p.start) return act(c, p);
    setSel({ key: k, start: p.start });
  };
  const act = (c: IptvChannel, p: EpgProgramme) => {
    if (p.start > now) return toggleReminder({ id: reminderId(c, p), title: p.title, subtitle: c.name, start: p.start, accountId: c.accountId, streamId: c.streamId });
    navigate(c.catchup && p.stop <= now ? playLink(c.accountId, c.streamId, { start: p.start, end: p.stop }) : playLink(c.accountId, c.streamId));
  };

  const listName = filter === 'fav' ? 'Favorieten' : filter === 'recent' ? 'Recent bekeken' : filter === 'all' ? 'Alle kanalen' : channelDisplay(usedCats.find((c) => c.id === filter)?.name ?? '').name;
  const pickFilter = (f: string) => { setFilter(f); setLimit(PAGE); setSel(null); };

  return (
    <div className="lg">
      {errors.map((e) => <p key={e} className="placeholder-row error" role="alert">{e}</p>)}
      <aside className="lg-side" aria-label="Kanalen kiezen" data-focus-group>
        <label className="lg-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <span className="visually-hidden">Kanaal zoeken</span>
          <input type="search" placeholder="Zoek kanaal…" value={query} onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} />
        </label>
        <button type="button" className="lg-item" aria-pressed={filter === 'fav'} onClick={() => pickFilter('fav')}>
          <span className="lg-ic lg-ic-star"><StarIcon on size={20} /></span>Favorieten<span className="lg-cnt">{favCount}</span>
        </button>
        {recentCount > 0 && (
          <button type="button" className="lg-item" aria-pressed={filter === 'recent'} onClick={() => pickFilter('recent')}>
            <span className="lg-ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5M12 7v5l3 2" /></svg></span>Recent bekeken<span className="lg-cnt">{recentCount}</span>
          </button>
        )}
        <button type="button" className="lg-item" aria-pressed={filter === 'all'} onClick={() => pickFilter('all')}>
          <span className="lg-ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg></span>Alle kanalen<span className="lg-cnt">{(channels?.length ?? 0).toLocaleString('nl-NL')}</span>
        </button>
        <a className="lg-item" {...linkProps('/live')}>
          <span className="lg-ic lg-ic-live"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m12 7 4 3-1.5 4.5h-5L8 10z" /></svg></span>Live sport
        </a>
        {usedCats.length > 1 && (
          <>
            <div className="lg-label">Mappen</div>
            <div className="lg-cats">
              {usedCats.map((c) => {
                const d = channelDisplay(c.name);
                return (
                  <button key={c.id} type="button" className="lg-cat" aria-pressed={filter === c.id} onClick={() => pickFilter(c.id)} title={c.name}>
                    {d.country && <span className="lg-tag">{d.country}</span>}
                    <span className="lg-cat-name">{d.name}</span>
                    <span className="lg-cnt">{c.n}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </aside>

      <div className="lg-main">
        {featured && <Featured c={featured.c} p={featured.p} now={now} number={numbers.get(keyOf(featured.c))} onFav={() => toggleFav(featured.c)} onAct={act} />}

        <div className="lg-bar">
          <strong>{listName}</strong>
          <span className="subtle">{visible.length.toLocaleString('nl-NL')} {visible.length === 1 ? 'kanaal' : 'kanalen'}</span>
          <div className="lg-bar-nav">
            <button type="button" className="lg-round" aria-label="Eerder" onClick={() => setShift((x) => x - 1)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
            </button>
            <span className="lg-day">{dayLabel(from)} · {clock(from)}–{clock(to)}</span>
            <button type="button" className="lg-round" aria-label="Later" onClick={() => setShift((x) => x + 1)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
            </button>
            <button type="button" className="btn btn-sm" disabled={shift === 0} onClick={() => setShift(0)}>Nu</button>
          </div>
        </div>

        {channels === null && <p className="placeholder-row">Kanalen laden…</p>}
        {channels && visible.length === 0 && (
          <p className="placeholder-row">{filter === 'fav' ? 'Nog geen favorieten. Kies Alle kanalen en klik op ☆ bij een kanaal.' : 'Geen kanalen gevonden.'}</p>
        )}

        {shown.length > 0 && (
          <div className="lg-grid" role="grid" aria-label="Programmagids">
            <div className="lg-row lg-head" role="row">
              <div className="lg-ch-head" />
              <div className="lg-track">
                {ticks.map((t) => <span key={t} className="lg-tick" style={{ left: pct(t) }}>{clock(t)}</span>)}
                {nowInView && <span className="lg-now-pill" style={{ left: pct(now) }}>{clock(now)}</span>}
              </div>
            </div>
            {shown.map((c) => {
              const key = keyOf(c);
              const progs = epg[key] ?? [];
              const d = channelDisplay(c.name);
              const active = featured?.c === c;
              return (
                <div key={key} className={`lg-row ${active ? 'is-active' : ''}`} role="row" data-focus-group>
                  <div className="lg-ch" role="rowheader">
                    <a className="lg-ch-link" {...linkProps(playLink(c.accountId, c.streamId))} title={c.name}>
                      <span className="lg-num">{numbers.get(key)}</span>
                      <ChannelLogo src={c.logo} name={c.name} size={36} />
                      <span className="lg-ch-text">
                        <span className="lg-ch-name">{c.eventTitle ? `${c.eventLive ? '● ' : ''}${c.eventTitle}` : d.name}</span>
                        {(d.country || c.eventTitle) && <span className="lg-ch-sub">{c.eventTitle ? d.name : d.country}</span>}
                      </span>
                    </a>
                    <button type="button" className={`lg-star ${c.favorite ? 'is-on' : ''}`} onClick={() => toggleFav(c)} aria-pressed={c.favorite} aria-label={c.favorite ? `${c.name} uit favorieten` : `${c.name} als favoriet`}>
                      <StarIcon on={c.favorite} />
                    </button>
                  </div>
                  <div className="lg-track">
                    {progs.length === 0 && (
                      <button type="button" className="lg-prog lg-prog-empty" style={{ left: 0, width: '100%' }} onClick={() => navigate(playLink(c.accountId, c.streamId))}>
                        {c.epgId ? 'Geen gids-informatie · klik om te kijken' : 'Geen gids voor dit kanaal · klik om te kijken'}
                      </button>
                    )}
                    {progs.filter((p) => p.stop > from && p.start < to).map((p) => {
                      const isNow = p.start <= now && p.stop > now;
                      const past = p.stop <= now;
                      const later = p.start > now;
                      const reminded = later && hasReminder(reminderId(c, p));
                      const picked = featured?.c === c && featured.p?.start === p.start;
                      return (
                        <button
                          type="button"
                          key={`${p.start}-${p.title}`}
                          className={`lg-prog ${isNow ? 'is-now' : ''} ${past ? 'is-past' : ''} ${picked ? 'is-picked' : ''} ${reminded ? 'is-reminded' : ''}`}
                          style={{ left: pct(p.start), width: `calc(${pct(p.stop)} - ${pct(p.start)} - 4px)` }}
                          onClick={() => choose(c, p)}
                          onFocus={() => setSel({ key, start: p.start })}
                          aria-label={`${p.title}, ${clock(p.start)} tot ${clock(p.stop)}${picked ? ', nog een keer klikken om te kijken' : ''}`}
                        >
                          <span className="lg-prog-title">{p.title}</span>
                          <span className="lg-prog-time">{reminded ? <><BellIcon /> </> : null}{c.catchup && past ? '⟲ ' : ''}{clock(p.start)} – {clock(p.stop)}</span>
                          {isNow && <span className="lg-prog-bar" style={{ width: `${Math.round(((now - p.start) / (p.stop - p.start)) * 100)}%` }} />}
                        </button>
                      );
                    })}
                    {nowInView && <span className="lg-now" style={{ left: pct(now) }} aria-hidden="true" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {visible.length > limit && (
          <div style={{ textAlign: 'center', padding: 'calc(var(--u) * 2)' }}>
            <button className="btn" onClick={() => setLimit((l) => l + PAGE)}>Meer kanalen ({visible.length - limit})</button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Bovenaan de gids: het gekozen programma met knoppen; rechts het zenderlogo (geen live voorbeeld: dat kost een IPTV-verbinding). */
function Featured({ c, p, now, number, onFav, onAct }: { c: IptvChannel; p: EpgProgramme | null; now: number; number?: number; onFav: () => void; onAct: (c: IptvChannel, p: EpgProgramme) => void }) {
  useReminders();
  const d = channelDisplay(c.name);
  const isNow = !!p && p.start <= now && p.stop > now;
  const past = !!p && p.stop <= now;
  const later = !!p && p.start > now;
  const left = p && isNow ? Math.max(1, Math.round((p.stop - now) / 60_000)) : 0;
  const reminded = !!p && later && hasReminder(reminderId(c, p));
  return (
    <section className="lg-feat" aria-label="Gekozen programma">
      <div className="lg-feat-info">
        <div className="lg-feat-ch">
          <ChannelLogo src={c.logo} name={c.name} size={30} />
          <span>{number ? `${number} · ` : ''}{d.name}</span>
          {d.country && <span className="lg-tag">{d.country}</span>}
        </div>
        <h2>{p?.title ?? d.name}</h2>
        {p && (
          <div className="lg-feat-meta">
            {isNow && <LiveBadge />}
            <span>{later || past ? `${dayLabel(p.start)} · ` : ''}{clock(p.start)} – {clock(p.stop)}</span>
            {isNow && <span>· nog {left} min</span>}
            {past && c.catchup && <span>· terugkijken</span>}
          </div>
        )}
        {p && isNow && <div className="lg-feat-progress" aria-hidden="true"><span style={{ width: `${Math.round(((now - p.start) / (p.stop - p.start)) * 100)}%` }} /></div>}
        {p?.desc ? <p className="lg-feat-desc">{p.desc}</p> : <p className="lg-feat-desc subtle">{p ? 'Geen beschrijving in de gids.' : 'Geen gids-informatie voor dit moment.'}</p>}
        <div className="lg-feat-actions" data-focus-group>
          {(!p || isNow) && (
            <a className="btn btn-primary" {...linkProps(playLink(c.accountId, c.streamId))}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z" /></svg>Kijken
            </a>
          )}
          {p && isNow && c.catchup && (
            <a className="btn" {...linkProps(playLink(c.accountId, c.streamId, { start: p.start, end: p.stop }))}>Vanaf begin</a>
          )}
          {p && past && c.catchup && (
            <a className="btn btn-primary" {...linkProps(playLink(c.accountId, c.streamId, { start: p.start, end: p.stop }))}>⟲ Terugkijken</a>
          )}
          {p && past && !c.catchup && <a className="btn" {...linkProps(playLink(c.accountId, c.streamId))}>Naar de zender</a>}
          {p && later && (
            <button type="button" className={`btn ${reminded ? '' : 'btn-primary'}`} onClick={() => onAct(c, p)}><BellIcon /> {reminded ? 'Herinnering uitzetten' : 'Herinner mij'}</button>
          )}
          <button type="button" className={`btn lg-fav ${c.favorite ? 'is-on' : ''}`} aria-pressed={c.favorite} aria-label={c.favorite ? 'Uit favorieten' : 'Als favoriet'} onClick={onFav}><StarIcon on={c.favorite} /></button>
        </div>
      </div>
      <a className="lg-feat-art" {...linkProps(playLink(c.accountId, c.streamId))} aria-label={`${d.name} kijken`}>
        {isNow && <span className="lg-feat-live"><LiveBadge /></span>}
        {c.logo ? <img src={c.logo} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="lg-feat-art-name">{d.name}</span>}
        <span className="lg-feat-play" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4v16l13-8z" /></svg></span>
      </a>
    </section>
  );
}

function reminderId(c: IptvChannel, p: EpgProgramme): string {
  return `epg:${c.accountId}:${c.streamId}:${p.start}`;
}

export { LiveBadge };
